// ============================================================================
// MilestonesEditor — checklist d'avancement d'un comite (E3a, F-413..F-420)
// ============================================================================
// Portage du PoC components/comites/milestones-editor.tsx : toute mutation
// (cocher, renommer, reordonner, supprimer, ajouter) reecrit l'array COMPLET
// (M-404, max 50 — borne serveur) en optimiste avec ROLLBACK sur echec (F-420).
// NT-L15-01 : target_date par jalon transporte mais JAMAIS editable a l'UI.
// Garde reelle au SERVEUR (management + membre, GA-02) — `canEdit` n'est que
// l'affordance UI renvoyee par l'API (viewer.canEditMilestones).
// Lecture seule (!canEdit) : checklist sans controles (parite PoC).

import { useState } from 'react';
import { Check, ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { GsProgressBar, GsIconButton, GS_COMPONENTS } from '@gs/ui';
import { useTranslation } from '../../i18n';
import { useSaveMilestones, type CommitteeMilestone } from '../../hooks/useCommittees';

export function MilestonesEditor({
  committeeId,
  initial,
  canEdit,
}: {
  committeeId: string;
  initial: CommitteeMilestone[];
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const save = useSaveMilestones(committeeId);
  const [items, setItems] = useState<CommitteeMilestone[]>(
    () => [...initial].sort((a, b) => a.sortOrder - b.sortOrder),
  );
  const [newTitle, setNewTitle] = useState('');
  const [error, setError] = useState<string | null>(null);

  const done = items.filter((m) => m.done).length;
  const pending = save.isPending;

  // Optimiste + rollback (F-420) : sort_order reindexe 0..n-1 (parite PoC).
  function persist(next: CommitteeMilestone[]) {
    const normalized = next.map((m, i) => ({ ...m, sortOrder: i }));
    const prev = items;
    setItems(normalized);
    setError(null);
    save.mutate(normalized, {
      onError: () => {
        setItems(prev);
        setError(t('gest:comites.milestones.saveError'));
      },
    });
  }

  function toggle(id: string) {
    persist(items.map((m) => (m.id === id ? { ...m, done: !m.done } : m)));
  }

  function remove(id: string) {
    persist(items.filter((m) => m.id !== id));
  }

  function move(index: number, dir: -1 | 1) {
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    persist(next);
  }

  function add() {
    const title = newTitle.trim();
    if (!title) return;
    persist([
      ...items,
      // id UUID genere client (parite PoC) ; target_date null (NT-L15-01).
      { id: crypto.randomUUID(), title, done: false, targetDate: null, sortOrder: items.length },
    ]);
    setNewTitle('');
  }

  // Renommage : maj locale a la frappe, persistance au blur (parite PoC F-415).
  function rename(id: string, title: string) {
    setItems((prev) => prev.map((m) => (m.id === id ? { ...m, title } : m)));
  }
  function commitRename(id: string) {
    const m = items.find((x) => x.id === id);
    if (!m || !m.title.trim()) return;
    persist(items);
  }

  return (
    <div className="flex flex-col gap-3">
      <GsProgressBar value={done} max={items.length} label />

      {items.length === 0 ? (
        <p className="text-field text-slate-500 dark:text-slate-400">
          {t('gest:comites.milestones.empty')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((m, i) => (
            <li
              key={m.id}
              className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-800"
            >
              <button
                type="button"
                onClick={() => canEdit && toggle(m.id)}
                disabled={!canEdit || pending}
                aria-label={m.done ? t('gest:comites.milestones.markTodo') : t('gest:comites.milestones.markDone')}
                className={`flex h-5 w-5 shrink-0 touch-manipulation items-center justify-center rounded-sm border transition-colors ${
                  m.done
                    ? 'border-green-500 bg-green-50 text-green-600 dark:bg-green-950 dark:text-green-400'
                    : 'border-slate-300 text-transparent dark:border-slate-600'
                } ${canEdit ? 'cursor-pointer' : 'cursor-default'}`}
              >
                <Check size={12} aria-hidden="true" />
              </button>

              {canEdit ? (
                <input
                  value={m.title}
                  onChange={(e) => rename(m.id, e.target.value)}
                  onBlur={() => commitRename(m.id)}
                  maxLength={200}
                  aria-label={t('gest:comites.milestones.renameLabel')}
                  className={`${GS_COMPONENTS.input} h-8 flex-1`}
                />
              ) : (
                <span
                  className={`flex-1 text-field ${
                    m.done
                      ? 'text-slate-400 line-through dark:text-slate-500'
                      : 'text-slate-800 dark:text-slate-100'
                  }`}
                >
                  {m.title}
                </span>
              )}

              {canEdit ? (
                <div className="flex shrink-0 items-center gap-0.5">
                  <GsIconButton
                    icon={<ChevronUp size={15} aria-hidden="true" />}
                    tooltip={t('gest:comites.milestones.moveUp')}
                    aria-label={t('gest:comites.milestones.moveUp')}
                    size="sm"
                    variant="ghost"
                    disabled={pending || i === 0}
                    onClick={() => move(i, -1)}
                  />
                  <GsIconButton
                    icon={<ChevronDown size={15} aria-hidden="true" />}
                    tooltip={t('gest:comites.milestones.moveDown')}
                    aria-label={t('gest:comites.milestones.moveDown')}
                    size="sm"
                    variant="ghost"
                    disabled={pending || i === items.length - 1}
                    onClick={() => move(i, 1)}
                  />
                  <GsIconButton
                    icon={<Trash2 size={15} aria-hidden="true" />}
                    tooltip={t('gest:comites.milestones.delete')}
                    aria-label={t('gest:comites.milestones.delete')}
                    size="sm"
                    variant="ghost"
                    disabled={pending}
                    onClick={() => remove(m.id)}
                  />
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {canEdit ? (
        <div className="flex items-center gap-2">
          <input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add(); // F-418 : Enter ajoute
              }
            }}
            placeholder={t('gest:comites.milestones.addPlaceholder')}
            maxLength={200}
            aria-label={t('gest:comites.milestones.addPlaceholder')}
            className={`${GS_COMPONENTS.input} h-9 flex-1`}
          />
          <button
            type="button"
            onClick={add}
            disabled={pending || !newTitle.trim()}
            className={`${GS_COMPONENTS.btnSecondary} inline-flex touch-manipulation items-center gap-1.5`}
          >
            <Plus size={16} aria-hidden="true" />
            {t('gest:comites.milestones.add')}
          </button>
        </div>
      ) : null}

      {error ? (
        <p role="alert" className="text-field text-red-600 dark:text-red-400">{error}</p>
      ) : null}
    </div>
  );
}
